import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { Toast } from "barte-design-system";
import { createPortal } from "react-dom";

type ToastVariant = "success" | "warning" | "error" | "info";

interface ToastItem {
  id: string;
  variant: ToastVariant;
  title: string;
  description?: string;
  duration?: number;
}

interface ToastContextData {
  addToast: (toast: Omit<ToastItem, "id">) => void;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextData>({} as ToastContextData);

// Variável para armazenar a referência do addToast (movida para cima)
let toastRef: ((toast: Omit<ToastItem, "id">) => void) | null = null;

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const addToast = useCallback((toast: Omit<ToastItem, "id">) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newToast: ToastItem = {
      ...toast,
      id,
      duration: toast.duration ?? 5000,
    };

    setToasts((prev) => [...prev, newToast]);

    // Auto remove after duration
    if (newToast.duration && newToast.duration > 0) {
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, newToast.duration);
    }
  }, []);

  // Setar a referência global quando o provider montar
  useEffect(() => {
    toastRef = addToast;
    return () => {
      toastRef = null;
    };
  }, [addToast]);

  return (
    <ToastContext.Provider value={{ addToast, removeToast }}>
      {children}
      {createPortal(
        <div
          style={{
            position: "fixed",
            top: "20px",
            right: "20px",
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            zIndex: 9999,
            maxWidth: "400px",
          }}
        >
          {toasts.map((toast) => (
            <Toast
              key={toast.id}
              variant={toast.variant}
              title={toast.title}
              description={toast.description}
              duration={4000}
              onClose={() => removeToast(toast.id)}
            />
          ))}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};

// Função global para mostrar toast (compatível com a API antiga)
export const showToastBarte = ({
  message,
  type = "error",
  title,
}: {
  message: string;
  type?: "warning" | "success" | "error" | "info";
  title?: string;
}) => {
  if (toastRef) {
    const titleMap: Record<string, string> = {
      success: "Sucesso",
      error: "Erro",
      warning: "Atenção",
      info: "Informação",
    };

    toastRef({
      variant: type,
      title: title ?? titleMap[type] ?? "Notificação",
      description: message,
    });
  } else {
    console.warn("ToastProvider não foi inicializado. Certifique-se de que o ToastProvider está envolvendo sua aplicação.");
  }
};
